import { ItemSearch } from "@models/item";
import { buildSpatieSort } from "../shared/utils/buildSpatieSort";
import { normalizeCustomDataFilter } from "../shared/utils/normalizeCustomDataFilter";

export type ItemInclude = "customData" | "entries";

export type ItemSortField =
  | "id"
  | "sku"
  | "name"
  | "group"
  | "manufacturer"
  | "model"
  | "price"
  | "cost"
  | "created_at"
  | "updated_at";

export type ItemSort = ItemSortField | `-${ItemSortField}`;

const ITEM_SORT_FIELDS: ItemSortField[] = [
  "id",
  "sku",
  "name",
  "group",
  "manufacturer",
  "model",
  "price",
  "cost",
  "created_at",
  "updated_at",
];

export const ITEM_BROWSE_INCLUDES: ItemInclude[] = ["customData", "entries"];
export const ITEM_BROWSE_PER_PAGE = 10;

export interface ItemSearchParams {
  filter?: Partial<ItemSearch>;
  include?: ItemInclude[];
  sort?: ItemSort[];
  per_page?: number;
  cursor?: string;
}

/**
 * Strips empty custom_data entries and normalizes custom_data filter shape for item search.
 * @param filter ItemSearch - Raw item search filter from browse UI.
 * @returns ItemSearch - Filter safe to send to the items search endpoint.
 */
export function normalizeItemSearchFilter(filter: ItemSearch): ItemSearch {

  // Map custom_data array into API-expected search filter shape
  const custom_data = normalizeCustomDataFilter(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;

  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

/**
 * Builds the POST body for the items Spatie browse/search endpoint.
 * @param params ItemSearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - Request body fields for items/search.
 */
export function getItemSearchBody(params: ItemSearchParams) {

  const { filter: rawFilter, include, sort, per_page = ITEM_BROWSE_PER_PAGE, cursor } = params;

  // Normalize filter fields (e.g. custom data) for Spatie search API
  const filter = rawFilter ? normalizeItemSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
  };
}

/**
 * Builds a Spatie sort string for item browse from column key and order.
 * @param sortKey string - Column field name from the browse table.
 * @param order "ASC" | "DESC" - Sort direction from the browse table.
 * @returns ItemSort - Spatie sort token accepted by the items search API.
 */
export function buildItemSort(sortKey: string, order: "ASC" | "DESC"): ItemSort {

  return buildSpatieSort(sortKey, order, ITEM_SORT_FIELDS, "id") as ItemSort;
}
